import type { Movimentacao, StatusOperacao } from '../../api/movimentacoes.js';
import type { TomStatus } from '../../componentes/ChipStatus.js';
import type { OpcaoFiltro } from '../../componentes/Filtros.js';

/**
 * As duas famílias de movimentação.
 *
 * Elas significam coisas diferentes para quem opera, então não podem se
 * parecer na tabela:
 *
 * - `entreDepositos` — trocou de depósito dentro da mesma filial. Está na fila
 *   de integração e vai virar lançamento no Senior.
 * - `origemIndeterminada` — o operador achou a caixa solta e não sabia de onde
 *   ela veio (UC08 4a). Nasce **retida** e fica esperando decisão humana. É a
 *   linha que alguém precisa olhar.
 *
 * Não existe mais movimentação no mesmo depósito: a API a recusa
 * (`ORIGEM_IGUAL_AO_DESTINO`), e movimentação entre filiais também
 * (`MOVIMENTACAO_ENTRE_FILIAIS`).
 */
export type Familia = 'entreDepositos' | 'origemIndeterminada';

/** Valor do filtro quando nenhuma família está recortada. */
export const FAMILIA_TODAS = 'todas';

type Classificavel = Pick<Movimentacao, 'origemIndeterminada'>;

export function familiaDaMovimentacao(movimentacao: Classificavel): Familia {
  return movimentacao.origemIndeterminada ? 'origemIndeterminada' : 'entreDepositos';
}

const ROTULO_DA_FAMILIA: Readonly<Record<Familia, string>> = {
  entreDepositos: 'Entre depósitos',
  origemIndeterminada: 'Origem indeterminada',
};

/**
 * Um tom por família — e nenhum deles repetido.
 *
 * O tom é reforço, nunca o único sinal: o chip carrega o rótulo escrito, e a
 * origem indeterminada ainda destaca a linha inteira (RNF-022).
 */
const TOM_DA_FAMILIA: Readonly<Record<Familia, TomStatus>> = {
  entreDepositos: 'andamento',
  origemIndeterminada: 'divergencia',
};

export function rotuloDaFamilia(familia: Familia): string {
  return ROTULO_DA_FAMILIA[familia];
}

export function tomDaFamilia(familia: Familia): TomStatus {
  return TOM_DA_FAMILIA[familia];
}

export const OPCOES_DE_FAMILIA: readonly OpcaoFiltro[] = [
  { valor: FAMILIA_TODAS, rotulo: 'Movimentação · todas' },
  { valor: 'entreDepositos', rotulo: ROTULO_DA_FAMILIA.entreDepositos },
  { valor: 'origemIndeterminada', rotulo: ROTULO_DA_FAMILIA.origemIndeterminada },
];

/** Legenda da tela: o que cada família quer dizer para quem opera. */
export const DESCRICAO_DAS_FAMILIAS: readonly {
  readonly familia: Familia;
  readonly texto: string;
}[] = [
  { familia: 'entreDepositos', texto: 'aguarda a fila de integração com o Senior.' },
  { familia: 'origemIndeterminada', texto: 'fica retida e espera decisão humana.' },
];

export type RecorteDeFamilia = {
  readonly origemIndeterminada?: boolean;
};

/** Recorte que alcança exatamente uma família. */
export function filtroDaFamilia(valor: string): RecorteDeFamilia {
  switch (valor) {
    case 'entreDepositos':
      return { origemIndeterminada: false };
    case 'origemIndeterminada':
      return { origemIndeterminada: true };
    default:
      return {};
  }
}

const ROTULO_DO_STATUS: Readonly<Record<StatusOperacao, string>> = {
  PendenteDispositivo: 'Pendente no aparelho',
  Registrada: 'Registrada',
  Retida: 'Retida',
  Enviando: 'Enviando',
  Consolidada: 'Consolidada',
  Rejeitada: 'Rejeitada',
  Cancelada: 'Cancelada',
  Superada: 'Superada',
};

const TOM_DO_STATUS: Readonly<Record<StatusOperacao, TomStatus>> = {
  PendenteDispositivo: 'andamento',
  Registrada: 'andamento',
  Retida: 'divergencia',
  Enviando: 'andamento',
  Consolidada: 'concluida',
  Rejeitada: 'falha',
  Cancelada: 'desativado',
  Superada: 'desativado',
};

export function rotuloDoStatus(status: StatusOperacao): string {
  return ROTULO_DO_STATUS[status];
}

export function tomDoStatus(status: StatusOperacao): TomStatus {
  return TOM_DO_STATUS[status];
}
